import React, {
  useState, useEffect, useContext, useMemo,
} from 'react';
import { useParams, useLocation } from 'react-router-dom';
import moment from 'moment-timezone';
import CircularProgress from '@mui/material/CircularProgress';
import { Button } from '@mui/material';
import { Navbar, Sidebar } from '../../components';
import './teamEngagement.css';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { UserContext } from '../../contexts/User';
import DateRangePicker from '../../components/dateRangePicker';
import TeamMemberCard from './TeamMemberCard';
import TeamEngagementScoreCard from './TeamEngagementScoreCard';
import TeamActivityCard from './TeamActivityCard';
import { getMemberRoleLevel } from '../../utils/permissions';
import { accountTypeLevel } from '../../constants/accountTypes';
import TimezoneSelect from '../../components/selectors/TimezoneSelect';
import ScheduleEmailModal from './ScheduleEmailModal';

function TeamView() {
  const { teamId } = useParams();
  const { search } = useLocation();
  const {
    activeWorkspace, redirect, member, user,
  } = useContext(UserContext);
  const [team, setTeam] = useState({});
  const today = useMemo(() => new Date(), []);
  const endOfLast7Days = useMemo(() => today, [today]);
  const startOfLast7Days = useMemo(() => {
    const start = new Date(today);
    start.setDate(today.getDate() - 6);
    if (start.getMonth() !== today.getMonth()) {
      start.setDate(1);
    }
    return start;
  }, [today]);
  const [date, setDate] = useState([startOfLast7Days, endOfLast7Days]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTimezone, setSelectedTimezone] = useState(
    activeWorkspace?.timezone ?? 'Members timezone',
  );

  useEffect(() => {
    const params = new URLSearchParams(search);
    const start = params.get('start');
    const end = params.get('end');
    if (start && end) {
      setDate([moment(start, 'YYYY-MM-DD HH:mm:ss').toDate(), moment(end, 'YYYY-MM-DD HH:mm:ss').toDate()]);
    }
  }, [search]);

  useEffect(() => {
    if (activeWorkspace?.timezone) {
      setSelectedTimezone(activeWorkspace.timezone);
    }
  }, [activeWorkspace?.timezone]);

  const fetchTeam = async () => {
    setIsLoading(true);
    const [startDate, endDate] = date;
    const startOfDay = moment(startDate).startOf('day').format('YYYY-MM-DD HH:mm:ss');
    const endOfDay = moment(endDate).endOf('day').format('YYYY-MM-DD HH:mm:ss');
    const { success, data } = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.getTeam(activeWorkspace.id, teamId),
      timeout: 3000000,
      params: {
        selectedTimezone,
        start: startOfDay,
        end: endOfDay,
      },
    });
    if (success) {
      setTeam(data.team);
    }
    setIsLoading(false);
  };

  const handleSelectDate = (_dateRange) => {
    setDate(_dateRange);
  };

  const handleSelectTimezone = async (timezone) => {
    setSelectedTimezone(timezone);
  };

  useEffect(() => {
    const isWorkspaceAdmin = getMemberRoleLevel(member) >= accountTypeLevel['Workspace admin'];
    if (isWorkspaceAdmin) return;
    const isTeamManager = member.teamMates.some((mate) => mate.teamId === parseInt(teamId, 10));
    if (!isTeamManager) redirect('/workspaces');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTeam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, selectedTimezone]);

  const timezoneOptions = [
    {
      heading: 'Members timezone',
      description: 'Members timezone',
      value: 'Members timezone',
    },
    {
      heading: 'Your timezone',
      description: user.timezone,
      value: user.timezone,
    },
    {
      heading: 'Workspace timezone',
      description: activeWorkspace.timezone ?? 'Etc/UTC',
      value: activeWorkspace.timezone ?? 'Etc/UTC',
    },
    {
      heading: 'Global timezone',
      description: 'Etc/UTC',
      value: 'Etc/UTC',
    },
  ];

  return (
    <div>
      {showScheduleModal && (
        <ScheduleEmailModal
          closeModal={() => setShowScheduleModal(false)}
          teamId={teamId}
          timezoneOptions={timezoneOptions}
        />
      )}
      <Navbar />
      <Sidebar />
      <div className="screen-wrapper">
        <div className="screen-header">
          <button
            type="button"
            className="back-button"
            onClick={() => redirect('/members/team-engagement')}
          >
            Back
          </button>
          <div className="screen-header-container d-flex justify-content-between gap-4">
            <h3 className="team-header-manage-member">
              {team.name}
              {' '}
              Engagement
            </h3>
            <div className="team-engagement-header-actions">
              <Button
                sx={{
                  marginLeft: 'auto',
                  font: 'Inter',
                  fontSize: '16px',
                  textTransform: 'none',
                }}
                variant="contained"
                onClick={() => {
                  setShowScheduleModal(true);
                }}
              >
                Schedule Email
              </Button>
              <DateRangePicker
                value={date}
                onChange={handleSelectDate}
                className="w-auto"
              />
              <TimezoneSelect
                value={selectedTimezone}
                label="Timezone"
                onChange={(e) => handleSelectTimezone(e.target.value)}
                options={timezoneOptions}
              />
            </div>
          </div>
        </div>

        <div className="screen-body mt-4">
          {
            isLoading && (
            <div className="loader-container">
              <CircularProgress sx={{ color: 'inherit' }} />
            </div>
            )
          }
          { !isLoading && (
          <>
            <div className="row">
              <div className="col-md-6">
                <TeamMemberCard team={team} />
              </div>
              <div className="col-md-6">
                <TeamEngagementScoreCard team={team} />
              </div>
            </div>
            <div className="row mt-3">
              <div className="col-12">
                <TeamActivityCard
                  team={team}
                  timezone={selectedTimezone}
                  dateRange={date}
                  curDate={moment(date?.[0] || new Date()).startOf('day').format('YYYY-MM-DD HH:mm:ss')}
                />
              </div>
            </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
}

export default TeamView;
